// Shared agent entry / README markdown helpers for Workspace AgentEntriesTool and TandemAgentEntriesTool.

export const AGENTS_ROOT = "/agents";
export const README_REL = `${AGENTS_ROOT}/README.md`;

const AGENTS_README_BODY_START = "<!-- AGENTS_README_BODY_START -->";
const AGENTS_README_BODY_END = "<!-- AGENTS_README_BODY_END -->";

const ENTRY_INDEX_LINE_RE =
  /^- \*\*\[agent-entry-(\d{3})\.md\]\(agent-entry-\d{3}\.md\)\*\*:\s*(.*)$/;

export const MAX_RANGE_INCLUSIVE = 100;

/** Max recent agent entries bundled into get-project-overview (latest by highest NNN). */
export const MAX_LATEST_OVERVIEW_ENTRIES = 50;

export const ENTRY_FILENAME_RE = /^agent-entry-(\d{3})\.md$/i;

export const DEFAULT_README_TEMPLATE = `# 🤖 Agents folder

${AGENTS_README_BODY_START}
This folder stores **agent entries** (numbered markdown files) for AI session continuity.

- Read this README and recent \`agent-entry-NNN.md\` files when starting work on the project.
- **When to write:** Do not add or update entries after every small change, a single short prompt, or only because you completed many tool calls. Create or update when the user explicitly asks, or when they clearly signal satisfaction or a hand-off to the next phase after verifying your work. Do not start a session by writing an entry unrelated to the user’s current task unless they asked for memory updates.
- Use \`read-agent-readme\` / \`write-agent-readme\` for the instructions block above the index; \`read-agent-entry\` / \`write-agent-entry\` for numbered entries (not raw paths under \`/agents/\`).
${AGENTS_README_BODY_END}

*Machine-maintained region below: index bullets are added/updated by \`write-agent-entry\` (\`readme_summary\`). The instructions block between the HTML comments above is replaced by \`write-agent-readme\`. Avoid hand-editing index lines so numbering stays consistent.*

## 🗂️ Agent Entry Index

---

`;

const AGENTS_README_STRUCTURE_EXAMPLE = `# 🤖 Agents folder

<!-- AGENTS_README_BODY_START -->
Editable instructions for agent memory go here.
<!-- AGENTS_README_BODY_END -->

## 🗂️ Agent Entry Index

- **[agent-entry-001.md](agent-entry-001.md)**: One-line summary for the entry
---
`;

function buildReadmeStructureGuidance(reason, mode = "body-or-index") {
  const requirement =
    mode === "index-only"
      ? 'Expected `agents/README.md` to contain a `## ... Agent Entry Index` heading and index lines shaped like `- **[agent-entry-001.md](agent-entry-001.md)**: Summary`.'
      : 'Expected `agents/README.md` to use the preferred body markers `<!-- AGENTS_README_BODY_START -->` / `<!-- AGENTS_README_BODY_END -->`, or at minimum the legacy `## ... Agent Entry Index` heading so the tool can recover the editable body.';

  return [
    reason,
    requirement,
    "Preferred structure:",
    AGENTS_README_STRUCTURE_EXAMPLE.trim(),
    "If the README does not match this shape, repair it with `write-file` or `edit-file` and retry.",
  ].join("\n\n");
}

export function normalizeSummaryForIndex(summary) {
  if (summary == null || typeof summary !== "string") {
    return "";
  }
  return summary.replace(/\s+/g, " ").trim();
}

function buildIndexLine(padded, summary) {
  return `- **[agent-entry-${padded}.md](agent-entry-${padded}.md)**: ${summary}`;
}

function findIndexHeaderLineIndex(lines) {
  return lines.findIndex(
    (l) => l.startsWith("##") && l.includes("Agent Entry Index")
  );
}

/**
 * Extract markdown body (instructions region) from full README text.
 */
export function extractReadmeBody(fullText) {
  const lines = fullText.split(/\r?\n/);
  const startIdx = lines.findIndex(
    (l) => l.trim() === AGENTS_README_BODY_START
  );
  const endIdx = lines.findIndex((l) => l.trim() === AGENTS_README_BODY_END);
  if (startIdx !== -1 && endIdx !== -1 && endIdx > startIdx) {
    return {
      success: true,
      body: lines.slice(startIdx + 1, endIdx).join("\n"),
      legacy: false,
    };
  }

  const idx = findIndexHeaderLineIndex(lines);
  if (idx === -1) {
    return {
      success: false,
      message: buildReadmeStructureGuidance(
        "README must include the editable body markers or a legacy index heading.",
        "body-or-index"
      ),
    };
  }

  const body = lines.slice(0, idx).join("\n").trimEnd();
  return { success: true, body, legacy: true };
}

/**
 * Replace body region; legacy files (no markers) get markers inserted before the index section.
 */
export function replaceReadmeBody(fullText, newBody) {
  const lines = fullText.split(/\r?\n/);
  const startIdx = lines.findIndex(
    (l) => l.trim() === AGENTS_README_BODY_START
  );
  const endIdx = lines.findIndex((l) => l.trim() === AGENTS_README_BODY_END);
  if (startIdx !== -1 && endIdx !== -1 && endIdx > startIdx) {
    const before = lines.slice(0, startIdx + 1);
    const after = lines.slice(endIdx);
    const middle = String(newBody).split(/\r?\n/);
    return {
      success: true,
      content: [...before, ...middle, ...after].join("\n"),
    };
  }

  const idx = findIndexHeaderLineIndex(lines);
  if (idx === -1) {
    return {
      success: false,
      message: buildReadmeStructureGuidance(
        'Cannot write README body: missing body markers and no "## ... Agent Entry Index" section found.',
        "body-or-index"
      ),
    };
  }

  const rest = lines.slice(idx);
  const middle = String(newBody).split(/\r?\n/);
  const injected = [
    AGENTS_README_BODY_START,
    ...middle,
    AGENTS_README_BODY_END,
    "",
  ];
  return {
    success: true,
    content: [...injected, ...rest].join("\n"),
  };
}

/**
 * Patch agents/README.md: replace existing index line for padded or insert in numeric order.
 * @returns {{ success: boolean, content?: string, message?: string }}
 */
export function patchReadmeIndexLines(lines, padded, summaryOneLine) {
  const headerIdx = findIndexHeaderLineIndex(lines);
  if (headerIdx === -1) {
    return {
      success: false,
      message: buildReadmeStructureGuidance(
        'README.md must contain a "## ... Agent Entry Index" section to update the entry list.',
        "index-only"
      ),
    };
  }

  const targetNum = parseInt(padded, 10);
  const newLine = buildIndexLine(padded, summaryOneLine);

  const bulletIndices = [];
  for (let i = headerIdx + 1; i < lines.length; i++) {
    const line = lines[i];
    if (/^---\s*$/.test(line)) {
      break;
    }
    if (ENTRY_INDEX_LINE_RE.test(line)) {
      bulletIndices.push(i);
    }
  }

  for (const idx of bulletIndices) {
    const m = lines[idx].match(ENTRY_INDEX_LINE_RE);
    if (m && parseInt(m[1], 10) === targetNum) {
      const next = [...lines];
      next[idx] = newLine;
      return { success: true, content: next.join("\n") };
    }
  }

  let insertAt;
  if (bulletIndices.length === 0) {
    insertAt = headerIdx + 1;
    while (insertAt < lines.length && lines[insertAt].trim() === "") {
      insertAt += 1;
    }
    if (insertAt < lines.length && /^---\s*$/.test(lines[insertAt])) {
      // insert before ---
    } else if (insertAt >= lines.length) {
      insertAt = lines.length;
    }
  } else {
    insertAt = -1;
    for (const idx of bulletIndices) {
      const m = lines[idx].match(ENTRY_INDEX_LINE_RE);
      const num = parseInt(m[1], 10);
      if (num > targetNum) {
        insertAt = idx;
        break;
      }
    }
    if (insertAt === -1) {
      insertAt = bulletIndices[bulletIndices.length - 1] + 1;
    }
  }

  const next = [...lines];
  next.splice(insertAt, 0, newLine);
  return { success: true, content: next.join("\n") };
}
